/**
 * Cross-subdomain host helpers for the leaving-site interstitial.
 *
 * Corgi Funds is served on two hosts that are treated as SEPARATE sites for
 * legal/disclosure purposes:
 *   - the main marketing + funds site  →  corgifunds.com       (+ www, + per-env)
 *   - the blog                         →  blog.corgifunds.com  (+ per-env)
 *
 * `apps/web` is deployed to BOTH (terraform `corgifunds_web_aliases`), so the
 * same SPA can render on either host. `<LeavingSiteModal>` intercepts any <a>
 * click that crosses between the two and warns before the navigation proceeds.
 *
 * Hosts are env-partitioned; helpers derive the sibling host from
 * `window.location` so dev/staging point at their own distribution, not prod's:
 *   prod     corgifunds.com              blog.corgifunds.com
 *   staging  staging.corgifunds.com      staging.blog.corgifunds.com
 *   dev      development.corgifunds.com  development.blog.corgifunds.com
 */

const PROD_MAIN_HOST = "corgifunds.com";
const PROD_BLOG_HOST = "blog.corgifunds.com";

/** True for any of the prod / staging / development blog subdomains. */
export function isBlogHost(hostname: string): boolean {
  return /^(staging\.|development\.)?blog\.corgifunds\.com$/.test(hostname);
}

/**
 * True for any corgifunds.com main-site host (apex, `www`, or a per-env
 * subdomain) that is NOT a blog host. localhost / preview hosts are neither,
 * so cross-host interception is a no-op there.
 */
export function isMainHost(hostname: string): boolean {
  if (isBlogHost(hostname)) return false;
  return hostname === PROD_MAIN_HOST || hostname.endsWith(".corgifunds.com");
}

/**
 * Should a navigation from `currentHost` to `targetHost` trigger the
 * leaving-site modal? Only when the click crosses BETWEEN the main site and
 * the blog (in either direction). Ordinary third-party links are NOT
 * intercepted.
 *
 * Matching is host-pattern based, not env-paired: a hardcoded prod-blog link
 * followed from a staging main host still warns. That's a deliberately safe
 * tradeoff — a spurious disclosure is harmless; a missed one is a compliance
 * gap. Official nav always uses the env-derived BLOG_BASE_URL/MAIN_BASE_URL.
 */
export function shouldIntercept(currentHost: string, targetHost: string): boolean {
  if (isBlogHost(currentHost) && isMainHost(targetHost)) return true;
  if (isMainHost(currentHost) && isBlogHost(targetHost)) return true;
  return false;
}

/** The blog host for the CURRENT environment (derived from `window.location`). */
export function blogHostForCurrent(): string {
  if (typeof window === "undefined") return PROD_BLOG_HOST;
  const h = window.location.hostname;
  if (h === "staging.corgifunds.com" || h === "staging.blog.corgifunds.com") {
    return "staging.blog.corgifunds.com";
  }
  if (h === "development.corgifunds.com" || h === "development.blog.corgifunds.com") {
    return "development.blog.corgifunds.com";
  }
  return PROD_BLOG_HOST;
}

/** The main-site host for the CURRENT environment (symmetric to {@link blogHostForCurrent}). */
export function mainHostForCurrent(): string {
  if (typeof window === "undefined") return PROD_MAIN_HOST;
  const h = window.location.hostname;
  if (h === "staging.corgifunds.com" || h === "staging.blog.corgifunds.com") {
    return "staging.corgifunds.com";
  }
  if (h === "development.corgifunds.com" || h === "development.blog.corgifunds.com") {
    return "development.corgifunds.com";
  }
  return PROD_MAIN_HOST;
}

/** `https://<blog-host>` for the current env — e.g. the nav "Blog" link target. */
export const BLOG_BASE_URL = `https://${blogHostForCurrent()}`;

/** `https://<main-host>` for the current env — used for blog → main absolute links. */
export const MAIN_BASE_URL = `https://${mainHostForCurrent()}`;

/** True when the page is currently served from a blog subdomain. */
export function isOnBlogSubdomain(): boolean {
  if (typeof window === "undefined") return false;
  return isBlogHost(window.location.hostname);
}
